import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  LabelValue,
  useMapSourceLabelValue,
  useStack9,
} from '@april9au/stack9-react';
import {
  S9ComponentConfigCategory,
  S9ComponentConfigRelease,
} from '@april9au/stack9-sdk';
import type { S9ComponentConfig } from '@april9au/stack9-sdk';
import { Form, FormItemProps, SelectProps, Select } from 'antd';
import { debounce } from 'lodash';

export const config: S9ComponentConfig = {
  name: 'CustomResourceMultiDropdown',
  icon: '',
  category: S9ComponentConfigCategory.STACK9,
  version: S9ComponentConfigRelease.ALPHA,
  hasState: true,
  allowChildren: false,
  props: [],
};

type Props = Pick<FormItemProps, 'label' | 'rules' | 'name'> &
  Pick<SelectProps, 'disabled' | 'status' | 'size' | 'placeholder'>;

const CustomResourceMultiDropdown = ({
  size,
  label,
  rules,
  disabled,
  status,
  name,
  placeholder,
}: Props) => {
  const optionLabelProp = 'title';
  const optionValueProp = 'id';

  const { queryService } = useStack9();
  const [options, setOptions] = useState<Array<LabelValue>>([]);
  const { mapLabelValue } = useMapSourceLabelValue('id', optionLabelProp);
  const form = Form.useFormInstance();

  const lastSearchedText = useRef<string | undefined>('');
  const onSearch = useCallback(
    async (searchText?: string) => {
      if (lastSearchedText.current === searchText) {
        return;
      }
      lastSearchedText.current = searchText;

      const {
        data: { results },
      } = await queryService.runNamedQuery<{ results: any[] }>(
        'resource',
        'getresources',
        {
          vars: {
            page: 0,
            limit: 20,
          },
          filters: {
            title: {
              key: 'title',
              operation: 'like',
              value: [searchText || ''],
            },
          },
        },
      );

      if (Array.isArray(results)) {
        setOptions(
          results
            ?.map(data => ({ ...data, id: String(data?.id || '') }))
            .map(mapLabelValue),
        );
      }
    },
    [queryService, mapLabelValue],
  );

  const onSearchDebounced = useMemo(() => debounce(onSearch, 300), [onSearch]);

  const fetchSelectedOptions = useCallback(
    async (optionValueProps: string[]) => {
      const {
        data: { results },
      } = await queryService.runNamedQuery<{ results: any[] }>(
        'resource_by_ids',
        'getresourcesbyids',
        {
          vars: {
            page: 0,
            limit: 20,
            ids: optionValueProps,
          },
        },
      );

      if (!results || results.length === 0) {
        return;
      }

      if (Array.isArray(results)) {
        setOptions(
          results
            ?.map(data => ({ ...data, id: String(data?.id || '') }))
            .map(mapLabelValue),
        );
      }
    },
    [queryService, mapLabelValue],
  );

  const initialOptionsFetched = useRef(false);
  const value = Form.useWatch([name], form);

  useEffect(() => {
    // this ensures that the selected options are fetched only once at first when the initial value is set
    if (initialOptionsFetched.current || !value) {
      return;
    }

    initialOptionsFetched.current = true;

    if (!value) {
      return;
    }

    let valueAsArray = [];

    try {
      valueAsArray = typeof value === 'string' ? JSON.parse(value) : value;
    } catch {
      valueAsArray = [];
    }

    valueAsArray = Array.isArray(valueAsArray) ? valueAsArray : [];

    fetchSelectedOptions(
      valueAsArray.map((item: Record<string, { id: string }>) =>
        String(item[optionValueProp]),
      ),
    );

    form.setFieldValue(name, JSON.stringify(valueAsArray));
  }, [fetchSelectedOptions, optionValueProp, value, form, name]);

  return (
    <Form.Item
      name={name}
      label={label}
      rules={rules}
      getValueProps={data => {
        let dataArray = [];
        try {
          const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

          dataArray = Array.isArray(parsedData) ? parsedData : [];
        } catch {
          dataArray = [];
        }

        return {
          value: dataArray.map((item: Record<string, { id: string }>) =>
            String(item[optionValueProp]),
          ),
        };
      }}
      getValueFromEvent={(selectedValues: string[]) =>
        JSON.stringify(
          selectedValues.map(value => ({ [optionValueProp]: value })),
        )
      }
    >
      <Select
        showSearch
        mode="multiple"
        autoClearSearchValue
        onSearch={onSearchDebounced}
        disabled={disabled}
        allowClear
        status={status}
        size={size}
        onClick={() => onSearch()}
        notFoundContent={<div>No records found</div>}
        options={options}
        filterOption={false}
        placeholder={placeholder}
      />
    </Form.Item>
  );
};

export default CustomResourceMultiDropdown;
